import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Form,
  Input,
  Button,
  Select,
  Card,
  Space,
  Alert,
  Divider,
  InputNumber,
  Table,
  Modal,
  Empty,
  Tooltip,
  Spin,
  Badge,
  Typography,
  Collapse,
  DatePicker,
  Checkbox,
  message,
} from "antd";
import { PlusOutlined, DeleteOutlined, EditOutlined, FilterOutlined } from "@ant-design/icons";
import { withRouter } from "react-router";
import moment from "moment";
import {
  getEmailSequenceById,
  updateEmailSequenceById,
} from "@api/email/emailSequence";
import { getEmailCampaignMetadata } from "@api/email/emailCampaign";
import { getActiveUserEmails } from "@api/email/users/userEmail";
import { MailOutlined, UserOutlined } from "@ant-design/icons";
import { SuccessNotification, ErrorNotification } from "@components";

const { Option } = Select;
const { Text } = Typography;

// Constants for the form (same as create form)
const SEQUENCE_TYPES = [
  { value: "onboarding", label: "Onboarding" },
  { value: "engagement", label: "Engagement" },
  { value: "milestone", label: "Milestone" },
  { value: "retention", label: "Retention" },
  { value: "inactivity", label: "Inactivity" },
];

const TRIGGER_TYPES = [
  { value: "signup", label: "User Signup" },
  { value: "firstGeneration", label: "First AI Generation" },
  { value: "creditsLow", label: "Credits Running Low" },
  { value: "inactivity", label: "User Inactivity" },
  { value: "timeAfterSignup", label: "Time After Signup" },
];

const TIME_UNITS = [
  { value: "minutes", label: "Minutes" },
  { value: "hours", label: "Hours" },
  { value: "days", label: "Days" },
];

// Segmentation options
const USAGE_PATTERNS = [
  { value: "any", label: "Any" },
  { value: "active", label: "Active (50+ generations/month)" },
  { value: "moderate", label: "Moderate (10-49 generations/month)" },
  { value: "inactive", label: "Inactive (<10 generations/month)" },
];

const ENGAGEMENT_LEVELS = [
  { value: "any", label: "Any" },
  { value: "high", label: "High (Active in last 7 days)" },
  { value: "medium", label: "Medium (Active in last 30 days)" },
  { value: "low", label: "Low (Inactive 30+ days)" },
];

const SUBSCRIPTION_TIERS = [
  { value: "any", label: "Any" },
  { value: "Free", label: "Free" },
  { value: "Starter", label: "Starter" },
  { value: "Growth", label: "Growth" },
  { value: "Pro", label: "Pro" },
  { value: "Enterprise", label: "Enterprise" },
];

const SUBSCRIPTION_STATUS = [
  { value: "any", label: "Any" },
  { value: "active", label: "Active" },
  { value: "trialing", label: "Trialing" },
  { value: "canceled", label: "Canceled" },
  { value: "past_due", label: "Past Due" },
];

const ACCOUNT_TYPES = [
  { value: "any", label: "Any" },
  { value: "local", label: "Local" },
  { value: "google", label: "Google" },
];

const USER_ROLES = [
  { value: "any", label: "Any" },
  { value: "admin", label: "Admin" },
  { value: "agent", label: "Agent" },
  { value: "superadmin", label: "Super Admin" },
  { value: "marketing", label: "Marketing" },
];

const EmailSequenceUpdateForm = ({ history, match }) => {
  const [values, setValues] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [editingEmail, setEditingEmail] = useState(null);
  const [emailModalVisible, setEmailModalVisible] = useState(false);
  const [availableEmails, setAvailableEmails] = useState({});

  useEffect(() => {
    fetchAvailableEmails();
  }, []);

  const fetchAvailableEmails = async () => {
    try {
      const response = await getActiveUserEmails();
      const emailsByDomain = response.data.reduce((acc, email) => {
        const domain = email.email.split("@")[1];
        if (!acc[domain]) {
          acc[domain] = [];
        }
        acc[domain].push(email);
        return acc;
      }, {});
      setAvailableEmails(emailsByDomain);
    } catch (error) {
      console.error("Error fetching available emails:", error);
      ErrorNotification("Failed to fetch available emails");
    }
  };

  // Fetch sequence data and templates on component mount
  useEffect(() => {
    const sequenceId = match.params.id;
    Promise.all([fetchSequence(sequenceId), fetchTemplates()]).finally(() => {
      setFetchingData(false);
    });
  }, [match.params.id]);

  // Function to fetch sequence data
  const fetchSequence = async (id) => {
    try {
      const response = await getEmailSequenceById(id);
      // Ensure segmentation data structure exists with defaults
      const sequenceData = {
        ...response.data,
        segmentation: response.data.segmentation || {
          behavior: {
            usagePattern: "any",
            engagementLevel: "any",
            lastActivityDaysAgo: {},
            totalGenerations: {},
            creditsUsed: {},
            creditsRemaining: {},
          },
          demographic: {
            signupDateRange: {},
            daysSinceSignup: {},
            subscriptionTier: ["any"],
            subscriptionStatus: ["any"],
            accountType: ["any"],
            userRole: ["any"],
          },
        },
      };
      setValues(sequenceData);
      // Make sure each email has emailsToUse populated
      if (!response.data.emails.every((email) => email.emailsToUse)) {
        ErrorNotification(
          "Some emails in the sequence are missing sending configuration"
        );
      }
    } catch (error) {
      console.error("Error fetching sequence:", error);
      ErrorNotification("Failed to fetch sequence data");
      history.goBack();
    }
  };

  // Function to fetch email templates
  const fetchTemplates = async () => {
    try {
      const response = await getEmailCampaignMetadata();
      setTemplates(response.data.htmlTemplates);
    } catch (error) {
      console.error("Error fetching templates:", error);
      ErrorNotification("Failed to fetch email templates");
    }
  };

  // Handle form field changes
  const handleFormChange = (field, value) => {
    setValues((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Handle trigger changes
  const handleTriggerChange = (field, value) => {
    setValues((prev) => ({
      ...prev,
      trigger: {
        ...prev.trigger,
        [field]: value,
      },
    }));
  };

  // Handle delay changes
  const handleDelayChange = (field, value) => {
    setValues((prev) => ({
      ...prev,
      trigger: {
        ...prev.trigger,
        delay: {
          ...prev.trigger.delay,
          [field]: value,
        },
      },
    }));
  };

  // Handle segmentation changes
  const handleSegmentationChange = (category, field, value) => {
    setValues((prev) => ({
      ...prev,
      segmentation: {
        ...prev.segmentation,
        [category]: {
          ...prev.segmentation[category],
          [field]: value,
        },
      },
    }));
  };

  // Handle range segmentation changes
  const handleRangeChange = (category, field, subField, value) => {
    setValues((prev) => ({
      ...prev,
      segmentation: {
        ...prev.segmentation,
        [category]: {
          ...prev.segmentation[category],
          [field]: {
            ...prev.segmentation[category][field],
            [subField]: value,
          },
        },
      },
    }));
  };

  // Email handlers
  const handleAddEmail = () => {
    setEditingEmail(null);
    setEmailModalVisible(true);
  };

  const handleEditEmail = (email, index) => {
    setEditingEmail({ ...email, index });
    setEmailModalVisible(true);
  };

  const handleDeleteEmail = (index) => {
    Modal.confirm({
      title: "Delete Email",
      content: "Are you sure you want to delete this email from the sequence?",
      okText: "Delete",
      okType: "danger",
      onOk: () => {
        setValues((prev) => ({
          ...prev,
          emails: prev.emails.filter((_, i) => i !== index),
        }));
      },
    });
  };

  const handleSaveEmail = (emailData) => {
    setValues((prev) => {
      const newEmails = [...prev.emails];
      if (editingEmail?.index !== undefined) {
        newEmails[editingEmail.index] = emailData;
      } else {
        newEmails.push(emailData);
      }
      return { ...prev, emails: newEmails };
    });
    setEmailModalVisible(false);
  };

  // Form submission
  const handleSubmit = async () => {
    // Validate form
    if (!values.name || !values.type || values.emails.length === 0) {
      return ErrorNotification("Please fill all required fields");
    }

    setLoading(true);
    try {
      await updateEmailSequenceById(match.params.id, values);
      SuccessNotification("Email sequence updated successfully");
      history.goBack();
    } catch (error) {
      console.error("Error updating sequence:", error);
      ErrorNotification("Failed to update email sequence");
    } finally {
      setLoading(false);
    }
  };

  // Email form component
  const EmailForm = ({ email, onSave, onCancel, emailModalVisible }) => {
    const [form] = Form.useForm();
    const [selectedEmails, setSelectedEmails] = useState([]);
    const [variableModalVisible, setVariableModalVisible] = useState(false);
    const [editingVariable, setEditingVariable] = useState(null);
    const [processing, setProcessing] = useState(false);

    // Initialize form values when email prop changes
    useEffect(() => {
      if (email) {
        const initialValues = {
          ...email,
          emailsToUse:
            email.emailsToUse?.map((e) =>
              e.useAlias
                ? `alias:${e.email}:${e.selectedAlias}`
                : `email:${e.email}`
            ) || [],
          variables: email.variables || [],
        };
        form.setFieldsValue(initialValues);
        setSelectedEmails(initialValues.emailsToUse);
      } else {
        form.resetFields();
        setSelectedEmails([]);
      }
    }, [email, form]);

    const formatEmailsForSelect = () => {
      return Object.entries(availableEmails).map(([domain, emails]) => {
        const options = emails
          .map((email) => {
            const baseOption = {
              value: `email:${email._id}`,
              label: (
                <Space>
                  <MailOutlined />
                  <span>{email.name || email.email.split("@")[0]}</span>
                  <Text type="secondary">&lt;{email.email}&gt;</Text>
                </Space>
              ),
              email: email,
            };

            const aliasOptions = (email.aliases || []).map((alias) => ({
              value: `alias:${email._id}:${alias}`,
              label: (
                <Space style={{ paddingLeft: 24 }}>
                  <UserOutlined />
                  <span>{alias.split("@")[0]}</span>
                  <Text type="secondary">&lt;{alias}&gt;</Text>
                  <Badge
                    count="Alias"
                    style={{
                      backgroundColor: "#e6f4ff",
                      color: "#1890ff",
                      fontSize: "12px",
                      border: "1px solid #91caff",
                    }}
                  />
                </Space>
              ),
              parentEmail: email,
              isAlias: true,
            }));

            return [baseOption, ...aliasOptions];
          })
          .flat();

        return {
          label: (
            <Space>
              <span>Domain: {domain}</span>
              <Text type="secondary">
                ({emails.length} email{emails.length !== 1 ? "s" : ""})
              </Text>
            </Space>
          ),
          options,
        };
      });
    };

    const handleEmailSelection = (selectedValues) => {
      setSelectedEmails(selectedValues);
      form.setFieldsValue({
        emailsToUse: selectedValues,
      });
    };

    const getSelectedEmailDetails = () => {
      const details = {
        totalEmails: selectedEmails.length,
        baseEmails: 0,
        aliases: 0,
        domains: new Set(),
      };

      selectedEmails.forEach((value) => {
        const [type, emailId, alias] = value.split(":");
        if (type === "email") {
          details.baseEmails++;
          const email = Object.values(availableEmails)
            .flat()
            .find((e) => e._id === emailId);
          if (email) {
            details.domains.add(email.email.split("@")[1]);
          }
        } else {
          details.aliases++;
          details.domains.add(alias.split("@")[1]);
        }
      });

      return details;
    };

    const handleVariableSave = (values) => {
      try {
        const currentVariables = form.getFieldValue("variables") || [];
        if (editingVariable !== null) {
          const newVariables = [...currentVariables];
          newVariables[editingVariable] = values;
          form.setFieldsValue({ variables: newVariables });
        } else {
          form.setFieldsValue({
            variables: [...currentVariables, values],
          });
        }
        setVariableModalVisible(false);
        setEditingVariable(null);
      } catch (error) {
        console.error("Error saving variable:", error);
        message.error("Failed to save variable");
      }
    };

    const handleVariableDelete = (index) => {
      try {
        const currentVariables = form.getFieldValue("variables") || [];
        const newVariables = currentVariables.filter((_, i) => i !== index);
        form.setFieldsValue({ variables: newVariables });
      } catch (error) {
        console.error("Error deleting variable:", error);
        message.error("Failed to delete variable");
      }
    };

    const handleSubmit = async () => {
      try {
        setProcessing(true);
        const values = await form.validateFields();

        const formattedValues = {
          ...values,
          emailsToUse: selectedEmails.map((value) => {
            const [type, emailId, alias] = value.split(":");
            return {
              email: emailId,
              useAlias: type === "alias",
              selectedAlias: type === "alias" ? alias : null,
            };
          }),
          variables: values.variables || [],
        };

        onSave(formattedValues);
        form.resetFields();
        setSelectedEmails([]);
        setProcessing(false);
      } catch (error) {
        console.error("Form validation error:", error);
        setProcessing(false);
        if (error.errorFields) {
          // Form validation error
          message.error("Please fill in all required fields correctly");
        } else {
          // Other errors
          message.error("An error occurred while saving the email");
        }
      }
    };

    const selectedDetails = getSelectedEmailDetails();

    const VariableForm = ({ onSave, onCancel }) => {
      const [variableForm] = Form.useForm();
      const isEditing = editingVariable !== null;
      const currentVariables = form.getFieldValue("variables") || [];
      const editingValue = isEditing ? currentVariables[editingVariable] : null;

      useEffect(() => {
        if (editingValue) {
          variableForm.setFieldsValue(editingValue);
        } else {
          variableForm.resetFields();
        }
      }, [editingValue, variableForm]);

      const handleVariableSubmit = async () => {
        try {
          const values = await variableForm.validateFields();
          onSave(values);
          variableForm.resetFields();
        } catch (error) {
          console.error("Variable form validation error:", error);
        }
      };

      return (
        <Modal
          title={isEditing ? "Edit Variable" : "Add Variable"}
          visible={variableModalVisible}
          onOk={handleVariableSubmit}
          onCancel={onCancel}
          destroyOnClose
        >
          <Form form={variableForm} layout="vertical">
            <Form.Item
              name="key"
              label="Variable Key"
              rules={[
                { required: true, message: "Please enter a variable key" },
                {
                  pattern: /^[a-zA-Z][a-zA-Z0-9_]*$/,
                  message:
                    "Key must start with a letter and contain only letters, numbers, and underscores",
                },
              ]}
            >
              <Input placeholder="e.g. user_name" />
            </Form.Item>
            <Form.Item
              name="value"
              label="Default Value"
              rules={[
                { required: true, message: "Please enter a default value" },
              ]}
            >
              <Input placeholder="Default value" />
            </Form.Item>
          </Form>
        </Modal>
      );
    };

    return (
      <Modal
        title={email ? "Edit Email" : "Add Email"}
        visible={emailModalVisible}
        onOk={handleSubmit}
        onCancel={onCancel}
        confirmLoading={processing}
        width={800}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            variables: [],
            delayAfterPrevious: {
              value: 0,
              unit: "hours",
            },
          }}
        >
          <Form.Item
            name="subject"
            label="Subject"
            rules={[{ required: true, message: "Please enter email subject" }]}
          >
            <Input placeholder="Enter email subject" />
          </Form.Item>

          <Form.Item
            name="templateId"
            label="HTML Template"
            rules={[{ required: true, message: "Please select a template" }]}
          >
            <Select placeholder="Select email template">
              {templates.map((template) => (
                <Option key={template._id} value={template._id}>
                  {template.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="emailsToUse"
            label="Sending Emails"
            required
            tooltip="Select email addresses and aliases to send this email from"
            rules={[
              { required: true, message: "Please select at least one email" },
            ]}
          >
            <Card>
              <Select
                mode="multiple"
                style={{ width: "100%", marginBottom: 16 }}
                placeholder="Select emails and aliases to send from..."
                value={selectedEmails}
                onChange={handleEmailSelection}
                optionLabelProp="label"
                optionFilterProp="label"
                options={formatEmailsForSelect()}
                listHeight={400}
                maxTagCount={5}
              />

              {selectedEmails.length > 0 && (
                <Card
                  size="small"
                  title="Selected Sending Configuration"
                  bordered={false}
                  style={{ background: "#f5f5f5" }}
                >
                  <Space
                    direction="vertical"
                    size="small"
                    style={{ width: "100%" }}
                  >
                    <Space>
                      <Badge
                        count={selectedDetails.totalEmails}
                        style={{ backgroundColor: "#1890ff" }}
                      />
                      <Text>Total sending addresses selected</Text>
                    </Space>
                    <Space>
                      <Badge
                        count={selectedDetails.baseEmails}
                        style={{ backgroundColor: "#52c41a" }}
                      />
                      <Text>Primary email addresses</Text>
                    </Space>
                    <Space>
                      <Badge
                        count={selectedDetails.aliases}
                        style={{ backgroundColor: "#722ed1" }}
                      />
                      <Text>Email aliases</Text>
                    </Space>
                    <Space>
                      <Badge
                        count={selectedDetails.domains.size}
                        style={{ backgroundColor: "#faad14" }}
                      />
                      <Text>Unique sending domains</Text>
                    </Space>
                  </Space>
                </Card>
              )}
            </Card>
          </Form.Item>

          <Form.Item name="fromName" label="From Name">
            <Input placeholder="Enter sender name" />
          </Form.Item>

          <Form.Item name="replyTo" label="Reply To">
            <Input placeholder="Enter reply-to email" />
          </Form.Item>

          <Form.Item label="Delay After Previous Email" required>
            <Input.Group compact>
              <Form.Item
                name={["delayAfterPrevious", "value"]}
                noStyle
                rules={[{ required: true, message: "Required" }]}
              >
                <InputNumber min={0} style={{ width: "60%" }} />
              </Form.Item>
              <Form.Item
                name={["delayAfterPrevious", "unit"]}
                noStyle
                rules={[{ required: true, message: "Required" }]}
              >
                <Select style={{ width: "40%" }}>
                  {TIME_UNITS.map((unit) => (
                    <Option key={unit.value} value={unit.value}>
                      {unit.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Input.Group>
          </Form.Item>

          <Form.Item name="variables" label="Variables">
            <Card>
              <Button
                type="dashed"
                onClick={() => {
                  setEditingVariable(null);
                  setVariableModalVisible(true);
                }}
                block
                icon={<PlusOutlined />}
              >
                Add Variable
              </Button>

              <Table
                style={{ marginTop: 16 }}
                dataSource={form.getFieldValue("variables") || []}
                rowKey={(record, index) => index}
                columns={[
                  {
                    title: "Key",
                    dataIndex: "key",
                    key: "key",
                  },
                  {
                    title: "Default Value",
                    dataIndex: "value",
                    key: "value",
                  },
                  {
                    title: "Actions",
                    key: "actions",
                    render: (_, record, index) => (
                      <Space>
                        <Button
                          type="text"
                          icon={<EditOutlined />}
                          onClick={() => {
                            setEditingVariable(index);
                            setVariableModalVisible(true);
                          }}
                        />
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => handleVariableDelete(index)}
                        />
                      </Space>
                    ),
                  },
                ]}
                pagination={false}
              />
            </Card>
          </Form.Item>
        </Form>

        <VariableForm
          onSave={handleVariableSave}
          onCancel={() => {
            setVariableModalVisible(false);
            setEditingVariable(null);
          }}
        />
      </Modal>
    );
  };

  // Email sequence table columns
  const emailColumns = [
    {
      title: "Subject",
      dataIndex: "subject",
      key: "subject",
      render: (text) => (
        <Tooltip title={text}>
          {text.length > 40 ? `${text.substring(0, 40)}...` : text}
        </Tooltip>
      ),
    },
    {
      title: "Template",
      dataIndex: "templateId",
      key: "templateId",
      render: (templateId) => {
        const template = templates.find((t) => t._id === templateId);
        return template?.name || "Template not found";
      },
    },
    {
      title: "Delay",
      key: "delay",
      render: (_, record) => (
        <span>
          {record.delayAfterPrevious.value} {record.delayAfterPrevious.unit}
        </span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_, record, index) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEditEmail(record, index)}
          />
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteEmail(index)}
          />
        </Space>
      ),
    },
  ];

  if (fetchingData || !values) {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <Spin
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "calc(100vh - 200px)",
            overflow: "hidden",
            fontSize: "20px",
          }}
        />
      </div>
    );
  }

  return (
    <Row>
      <Col span={24}>
        <Form layout="vertical" initialValues={values}>
          <Card title="Sequence Details">
            <Alert
              message="Updating Email Sequence"
              description="Changes to the sequence will only affect new users. Existing users will continue with their current sequence."
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />

            <Form.Item
              label="Sequence Name"
              required
              tooltip="A descriptive name for this email sequence"
            >
              <Input
                placeholder="Enter sequence name"
                value={values.name}
                onChange={(e) => handleFormChange("name", e.target.value)}
              />
            </Form.Item>

            <Form.Item label="Description">
              <Input.TextArea
                placeholder="Enter sequence description"
                value={values.description}
                onChange={(e) =>
                  handleFormChange("description", e.target.value)
                }
                rows={4}
              />
            </Form.Item>

            <Form.Item
              label="Sequence Type"
              required
              tooltip="The purpose or category of this sequence"
            >
              <Select
                value={values.type}
                onChange={(value) => handleFormChange("type", value)}
              >
                {SEQUENCE_TYPES.map((type) => (
                  <Option key={type.value} value={type.value}>
                    {type.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item label="Status">
              <Select
                value={values.active}
                onChange={(value) => handleFormChange("active", value)}
              >
                <Option value={true}>Active</Option>
                <Option value={false}>Inactive</Option>
              </Select>
            </Form.Item>
          </Card>

          <Divider />

          <Card title="Trigger Configuration">
            <Form.Item
              label="Trigger Event"
              required
              tooltip="The event that will start this sequence"
            >
              <Select
                value={values.trigger.eventType}
                onChange={(value) => handleTriggerChange("eventType", value)}
              >
                {TRIGGER_TYPES.map((type) => (
                  <Option key={type.value} value={type.value}>
                    {type.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label="Initial Delay"
              required
              tooltip="Delay before sending the first email after the trigger"
            >
              <Input.Group compact>
                <InputNumber
                  style={{ width: "60%" }}
                  min={0}
                  value={values.trigger.delay.value}
                  onChange={(value) => handleDelayChange("value", value)}
                />
                <Select
                  style={{ width: "40%" }}
                  value={values.trigger.delay.unit}
                  onChange={(value) => handleDelayChange("unit", value)}
                >
                  {TIME_UNITS.map((unit) => (
                    <Option key={unit.value} value={unit.value}>
                      {unit.label}
                    </Option>
                  ))}
                </Select>
              </Input.Group>
            </Form.Item>
          </Card>

          <Divider />

          <Card title="Audience Segmentation (Optional)">
            <Alert
              message="Define your target audience"
              description="Leave fields as 'Any' to include all users, or specify criteria to target specific segments."
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
            
            <Collapse ghost>
              <Collapse.Panel 
                header={
                  <Space>
                    <FilterOutlined />
                    <span>Behavior-based Segmentation</span>
                  </Space>
                } 
                key="behavior"
              >
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item label="Usage Pattern">
                      <Select
                        value={values.segmentation.behavior.usagePattern}
                        onChange={(value) => 
                          handleSegmentationChange("behavior", "usagePattern", value)
                        }
                      >
                        {USAGE_PATTERNS.map((pattern) => (
                          <Option key={pattern.value} value={pattern.value}>
                            {pattern.label}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="Engagement Level">
                      <Select
                        value={values.segmentation.behavior.engagementLevel}
                        onChange={(value) => 
                          handleSegmentationChange("behavior", "engagementLevel", value)
                        }
                      >
                        {ENGAGEMENT_LEVELS.map((level) => (
                          <Option key={level.value} value={level.value}>
                            {level.label}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
                
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item label="Last Activity (Days Ago)">
                      <Input.Group compact>
                        <InputNumber
                          style={{ width: "50%" }}
                          placeholder="Min"
                          min={0}
                          value={values.segmentation.behavior.lastActivityDaysAgo?.min}
                          onChange={(value) => 
                            handleRangeChange("behavior", "lastActivityDaysAgo", "min", value)
                          }
                        />
                        <InputNumber
                          style={{ width: "50%" }}
                          placeholder="Max"
                          min={0}
                          value={values.segmentation.behavior.lastActivityDaysAgo?.max}
                          onChange={(value) => 
                            handleRangeChange("behavior", "lastActivityDaysAgo", "max", value)
                          }
                        />
                      </Input.Group>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="Total Generations">
                      <Input.Group compact>
                        <InputNumber
                          style={{ width: "50%" }}
                          placeholder="Min"
                          min={0}
                          value={values.segmentation.behavior.totalGenerations?.min}
                          onChange={(value) => 
                            handleRangeChange("behavior", "totalGenerations", "min", value)
                          }
                        />
                        <InputNumber
                          style={{ width: "50%" }}
                          placeholder="Max"
                          min={0}
                          value={values.segmentation.behavior.totalGenerations?.max}
                          onChange={(value) => 
                            handleRangeChange("behavior", "totalGenerations", "max", value)
                          }
                        />
                      </Input.Group>
                    </Form.Item>
                  </Col>
                </Row>
                
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item label="Credits Used">
                      <Input.Group compact>
                        <InputNumber
                          style={{ width: "50%" }}
                          placeholder="Min"
                          min={0}
                          value={values.segmentation.behavior.creditsUsed?.min}
                          onChange={(value) => 
                            handleRangeChange("behavior", "creditsUsed", "min", value)
                          }
                        />
                        <InputNumber
                          style={{ width: "50%" }}
                          placeholder="Max"
                          min={0}
                          value={values.segmentation.behavior.creditsUsed?.max}
                          onChange={(value) => 
                            handleRangeChange("behavior", "creditsUsed", "max", value)
                          }
                        />
                      </Input.Group>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="Credits Remaining">
                      <Input.Group compact>
                        <InputNumber
                          style={{ width: "50%" }}
                          placeholder="Min"
                          min={0}
                          value={values.segmentation.behavior.creditsRemaining?.min}
                          onChange={(value) => 
                            handleRangeChange("behavior", "creditsRemaining", "min", value)
                          }
                        />
                        <InputNumber
                          style={{ width: "50%" }}
                          placeholder="Max"
                          min={0}
                          value={values.segmentation.behavior.creditsRemaining?.max}
                          onChange={(value) => 
                            handleRangeChange("behavior", "creditsRemaining", "max", value)
                          }
                        />
                      </Input.Group>
                    </Form.Item>
                  </Col>
                </Row>
              </Collapse.Panel>
              
              <Collapse.Panel 
                header={
                  <Space>
                    <FilterOutlined />
                    <span>Demographic Segmentation</span>
                  </Space>
                } 
                key="demographic"
              >
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item label="Days Since Signup">
                      <Input.Group compact>
                        <InputNumber
                          style={{ width: "50%" }}
                          placeholder="Min"
                          min={0}
                          value={values.segmentation.demographic.daysSinceSignup?.min}
                          onChange={(value) => 
                            handleRangeChange("demographic", "daysSinceSignup", "min", value)
                          }
                        />
                        <InputNumber
                          style={{ width: "50%" }}
                          placeholder="Max"
                          min={0}
                          value={values.segmentation.demographic.daysSinceSignup?.max}
                          onChange={(value) => 
                            handleRangeChange("demographic", "daysSinceSignup", "max", value)
                          }
                        />
                      </Input.Group>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="Signup Date Range">
                      <DatePicker.RangePicker
                        style={{ width: "100%" }}
                        value={[
                          values.segmentation.demographic.signupDateRange?.start ? 
                            moment(values.segmentation.demographic.signupDateRange.start) : null,
                          values.segmentation.demographic.signupDateRange?.end ? 
                            moment(values.segmentation.demographic.signupDateRange.end) : null,
                        ]}
                        onChange={(dates) => {
                          handleSegmentationChange("demographic", "signupDateRange", {
                            start: dates?.[0]?.toDate(),
                            end: dates?.[1]?.toDate(),
                          });
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item label="Subscription Tier">
                      <Select
                        mode="multiple"
                        value={values.segmentation.demographic.subscriptionTier}
                        onChange={(value) => 
                          handleSegmentationChange("demographic", "subscriptionTier", value)
                        }
                      >
                        {SUBSCRIPTION_TIERS.map((tier) => (
                          <Option key={tier.value} value={tier.value}>
                            {tier.label}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="Subscription Status">
                      <Select
                        mode="multiple"
                        value={values.segmentation.demographic.subscriptionStatus}
                        onChange={(value) => 
                          handleSegmentationChange("demographic", "subscriptionStatus", value)
                        }
                      >
                        {SUBSCRIPTION_STATUS.map((status) => (
                          <Option key={status.value} value={status.value}>
                            {status.label}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
                
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item label="Account Type">
                      <Select
                        mode="multiple"
                        value={values.segmentation.demographic.accountType}
                        onChange={(value) => 
                          handleSegmentationChange("demographic", "accountType", value)
                        }
                      >
                        {ACCOUNT_TYPES.map((type) => (
                          <Option key={type.value} value={type.value}>
                            {type.label}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="User Role">
                      <Select
                        mode="multiple"
                        value={values.segmentation.demographic.userRole}
                        onChange={(value) => 
                          handleSegmentationChange("demographic", "userRole", value)
                        }
                      >
                        {USER_ROLES.map((role) => (
                          <Option key={role.value} value={role.value}>
                            {role.label}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
              </Collapse.Panel>
            </Collapse>
          </Card>

          <Divider />

          <Card
            title="Email Sequence"
            extra={
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAddEmail}
              >
                Add Email
              </Button>
            }
          >
            {values.emails.length > 0 ? (
              <>
                <Alert
                  message={`${values.emails.length} email${
                    values.emails.length !== 1 ? "s" : ""
                  } in sequence`}
                  type="info"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
                <Table
                  columns={emailColumns}
                  dataSource={values.emails}
                  rowKey={(record, index) => index}
                  pagination={false}
                />
              </>
            ) : (
              <Empty description="No emails added to sequence yet" />
            )}
          </Card>

          <Divider />

          <Form.Item>
            <Space>
              <Button type="primary" onClick={handleSubmit} loading={loading}>
                Update Sequence
              </Button>
              <Button onClick={() => history.goBack()}>Cancel</Button>
            </Space>
          </Form.Item>

          <EmailForm
            email={editingEmail}
            onSave={handleSaveEmail}
            onCancel={() => setEmailModalVisible(false)}
            emailModalVisible={emailModalVisible} // Add this line
          />
        </Form>
      </Col>
    </Row>
  );
};

export default withRouter(EmailSequenceUpdateForm);
